const CHART_HEX = [
  'hsl(var(--ds-primary))',
  'hsl(var(--ds-secondary))',
  '#14b8a6',
  '#10b981',
  '#f59e0b',
  '#f97316',
  '#f43f5e',
  '#a855f7',
  '#06b6d4',
  '#84cc16',
  '#d946ef',
  '#0ea5e9'
];

const CHART_BG = [
  'bg-[hsl(var(--ds-primary))]',
  'bg-[hsl(var(--ds-secondary))]',
  'bg-teal-500',
  'bg-[hsl(var(--ds-success))]',
  'bg-amber-500',
  'bg-orange-500',
  'bg-rose-500',
  'bg-purple-500',
  'bg-cyan-500',
  'bg-lime-500',
  'bg-fuchsia-500',
  'bg-sky-500'
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, outerR, endAngle);
  const endOuter = polarToCartesian(cx, cy, outerR, startAngle);
  const startInner = polarToCartesian(cx, cy, innerR, startAngle);
  const endInner = polarToCartesian(cx, cy, innerR, endAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
    'Z'
  ].join(' ');
}

function buildSlices(data) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [, value]) => sum + Number(value || 0), 0) || 1;
  let angle = 0;
  return entries.map(([key, value], idx) => {
    const count = Number(value || 0);
    const sweep = (count / total) * 360;
    const slice = {
      key,
      value: count,
      pct: ((count / total) * 100).toFixed(1),
      startAngle: angle,
      endAngle: angle + sweep,
      color: CHART_HEX[idx % CHART_HEX.length],
      bgClass: CHART_BG[idx % CHART_BG.length]
    };
    angle += sweep;
    return slice;
  });
}

export function ChartTypeToggle({ mode, onChange }) {
  const options = [
    { id: 'bar', label: 'Bar' },
    { id: 'pie', label: 'Pie' },
    { id: 'combo', label: 'Combo' }
  ];
  return (
    <div className="inline-flex rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-0.5 text-[10px]">
      {options.map(opt => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2 py-0.5 rounded-full font-medium transition-colors ${
            mode === opt.id
              ? 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] shadow-sm'
              : 'text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function DonutPieChart({ data, onSliceClick, size = 120, labelFormat }) {
  const slices = buildSlices(data);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.42;
  const innerR = size * 0.24;

  if (!total) {
    return <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No data available.</p>;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
        {slices.map(slice => {
          if (slice.value <= 0) return null;
          const interactive = typeof onSliceClick === 'function';
          const path = describeArc(cx, cy, outerR, innerR, slice.startAngle, slice.endAngle);
          return (
            <path
              key={slice.key}
              d={path}
              fill={slice.color}
              className={interactive ? 'cursor-pointer hover:opacity-90' : ''}
              onClick={interactive ? () => onSliceClick(slice.key, slice.value) : undefined}
            >
              <title>
                {(labelFormat ? labelFormat(slice.key) : slice.key) + `: ${slice.value} (${slice.pct}%)`}
              </title>
            </path>
          );
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" className="fill-slate-800 text-[11px] font-semibold">
          {total}
        </text>
        <text x={cx} y={cy + 10} textAnchor="middle" className="fill-slate-500 text-[8px]">
          total
        </text>
      </svg>
      <div className="flex-1 min-w-0 space-y-1 w-full">
        {slices.map(slice => {
          const interactive = typeof onSliceClick === 'function';
          const Wrapper = interactive ? 'button' : 'div';
          const label = labelFormat ? labelFormat(slice.key) : slice.key;
          return (
            <Wrapper
              key={slice.key}
              type={interactive ? 'button' : undefined}
              onClick={interactive ? () => onSliceClick(slice.key, slice.value) : undefined}
              className={`w-full flex items-center gap-2 text-left text-xs ${interactive ? 'hover:bg-[hsl(var(--ds-muted))] rounded px-1 -mx-1' : ''}`}
            >
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: slice.color }} />
              <span className="truncate flex-1 capitalize">{label}</span>
              <span className="text-[hsl(var(--ds-muted-fg))] shrink-0">
                {slice.value} <span className="text-[hsl(var(--ds-muted-fg))]">({slice.pct}%)</span>
              </span>
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}

export function HorizontalBarChart({ data, onBarClick, labelFormat, colors = CHART_BG }) {
  const maxValue = Math.max(...Object.values(data || {}), 1);
  const entries = Object.entries(data || {});

  if (!entries.length) {
    return <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No data available.</p>;
  }

  return (
    <div className="space-y-1.5">
      {entries.map(([key, value], idx) => {
        const interactive = typeof onBarClick === 'function';
        const Wrapper = interactive ? 'button' : 'div';
        const label = labelFormat ? labelFormat(key) : key;
        return (
          <Wrapper
            key={key}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onBarClick(key, value) : undefined}
            className={`w-full text-left ${interactive ? 'group cursor-pointer rounded-lg px-1 -mx-1 hover:bg-[hsl(var(--ds-muted))]' : ''}`}
          >
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-xs font-medium capitalize truncate pr-2">{label}</span>
              <span className="text-xs text-[hsl(var(--ds-muted-fg))] shrink-0">{value}</span>
            </div>
            <div className="w-full bg-[hsl(var(--ds-muted))] h-4 rounded-sm overflow-hidden">
              <div
                className={`${colors[idx % colors.length]} h-full flex items-center justify-end pr-1 min-w-0 transition-opacity ${interactive ? 'group-hover:opacity-90' : ''}`}
                style={{ width: `${(value / maxValue) * 100}%` }}
              >
                {value > 0 && <span className="text-[10px] text-white font-medium">{value}</span>}
              </div>
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

export function ComboChart({ data, onSegmentClick, labelFormat }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <DonutPieChart data={data} onSliceClick={onSegmentClick} labelFormat={labelFormat} />
      <HorizontalBarChart data={data} onBarClick={onSegmentClick} labelFormat={labelFormat} />
    </div>
  );
}

export function AnalyticsChart({ mode, data, onSegmentClick, labelFormat }) {
  if (mode === 'pie') {
    return <DonutPieChart data={data} onSliceClick={onSegmentClick} labelFormat={labelFormat} />;
  }
  if (mode === 'combo') {
    return <ComboChart data={data} onSegmentClick={onSegmentClick} labelFormat={labelFormat} />;
  }
  return <HorizontalBarChart data={data} onBarClick={onSegmentClick} labelFormat={labelFormat} />;
}

const STAGE_COLORS = {
  submitted: 'bg-[hsl(var(--ds-primary))]',
  selected: 'bg-cyan-500',
  not_selected: 'bg-rose-500',
  interview: 'bg-yellow-500',
  shortlisted: 'bg-[hsl(var(--ds-primary)/0.75)]',
  offer: 'bg-purple-500',
  joined: 'bg-[hsl(var(--ds-success))]',
  relieved: 'bg-[hsl(var(--ds-success))]',
  rejected: 'bg-red-500'
};

const STAGE_LABELS = {
  submitted: 'Submitted',
  selected: 'Selected',
  not_selected: 'Not Selected',
  interview: 'Interview',
  shortlisted: 'Shortlisted',
  offer: 'Offer',
  joined: 'Joined',
  relieved: 'Relieved',
  rejected: 'Rejected'
};

const COMPARE_COLORS = {
  'Interview+': 'bg-[hsl(var(--ds-primary))]',
  Interview: 'bg-[hsl(var(--ds-primary))]',
  Joined: 'bg-[hsl(var(--ds-success))]',
  Offer: 'bg-purple-500',
  Assigned: 'bg-[hsl(var(--ds-secondary))]',
  Completed: 'bg-[hsl(var(--ds-primary)/0.65)]'
};

export function GroupedComparisonChart({ groups = [], onGroupClick, valueSuffix = '' }) {
  if (!groups.length) {
    return <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No data available.</p>;
  }

  const seriesKeys = [...new Set(groups.flatMap(g => Object.keys(g.values || {})))];
  const maxValue = Math.max(...groups.flatMap(g => Object.values(g.values || {})), 1);
  const interactive = typeof onGroupClick === 'function';

  return (
    <div className="space-y-3">
      {groups.map(group => {
        const Wrapper = interactive ? 'button' : 'div';
        return (
          <Wrapper
            key={group.id || group.label}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onGroupClick(group) : undefined}
            className={`w-full text-left ${interactive ? 'group rounded-lg p-1 -m-1 hover:bg-[hsl(var(--ds-muted))]' : ''}`}
          >
            <div className="text-xs font-medium text-[hsl(var(--ds-fg))] mb-1 truncate" title={group.fullLabel || group.label}>
              {group.label}
            </div>
            <div className="space-y-1">
              {seriesKeys.map(key => {
                const value = Number(group.values?.[key] ?? 0);
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] w-16 shrink-0">{key}</span>
                    <div className="flex-1 bg-[hsl(var(--ds-muted))] h-3.5 rounded-sm overflow-hidden">
                      <div
                        className={`${COMPARE_COLORS[key] || 'bg-[hsl(var(--ds-muted))]0'} h-full transition-opacity ${interactive ? 'group-hover:opacity-90' : ''}`}
                        style={{ width: `${(value / maxValue) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] w-10 text-right shrink-0">
                      {value}
                      {valueSuffix}
                    </span>
                  </div>
                );
              })}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

export function StageFunnelChart({ stageData = {}, onStageClick }) {
  const entries = Object.entries(stageData).filter(([, v]) => v > 0);
  const total = entries.reduce((sum, [, v]) => sum + v, 0) || 1;
  const maxValue = Math.max(...entries.map(([, v]) => v), 1);

  if (!entries.length) {
    return <p className="text-sm text-[hsl(var(--ds-muted-fg))]">No pipeline data.</p>;
  }

  return (
    <div className="space-y-2">
      {entries.map(([stage, value]) => {
        const interactive = typeof onStageClick === 'function';
        const Wrapper = interactive ? 'button' : 'div';
        const pctOfTotal = ((value / total) * 100).toFixed(1);
        return (
          <Wrapper
            key={stage}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onStageClick(stage, value) : undefined}
            className={`w-full text-left ${interactive ? 'group hover:bg-[hsl(var(--ds-muted))] rounded-lg p-1 -m-1' : ''}`}
          >
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium">{STAGE_LABELS[stage] || stage}</span>
              <span className="text-[hsl(var(--ds-muted-fg))]">
                {value} <span className="text-[hsl(var(--ds-muted-fg))]">({pctOfTotal}%)</span>
              </span>
            </div>
            <div className="w-full bg-[hsl(var(--ds-muted))] h-5 rounded overflow-hidden">
              <div
                className={`${STAGE_COLORS[stage] || 'bg-[hsl(var(--ds-muted))]0'} h-full transition-opacity ${interactive ? 'group-hover:opacity-90' : ''}`}
                style={{ width: `${(value / maxValue) * 100}%` }}
              />
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}
